/**
 * GoodGuardian — Settings Screen
 * Tabs: Profile | Notifications | Display | Security | Dashboard
 */
import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import AppHeader from '@/components/layout/AppHeader'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import { useApp } from '@/context/AppContext'
import { useTheme } from '@/hooks/useTheme'
import {
  getBiometryInfo, authenticate as bioAuthenticate,
  isBiometricEnabled, enableBiometric, disableBiometric,
  biometryLabel, biometryIcon,
} from '@/utils/biometric'
import { getDashPrefs, saveDashPrefs, DASH_DEFAULT_MODULES } from '@/screens/Dashboard'

// ── Situations (shared with Onboarding) ──────────────────────────────────────
const SITUATIONS = [
  { value: 'divorcing',   emoji: '⚖️',  label: 'Going through divorce',  desc: 'Currently in or anticipating divorce proceedings.' },
  { value: 'custody',     emoji: '👨‍👧', label: 'Custody dispute',        desc: 'Fighting for or defending custody rights.' },
  { value: 'cps',         emoji: '🏛️', label: 'CPS involvement',         desc: 'Navigating a CPS investigation or inquiry.' },
  { value: 'coparenting', emoji: '🤝',  label: 'Managing co-parenting',  desc: 'Already divorced; managing an existing arrangement.' },
]

// ── Helpers ───────────────────────────────────────────────────────────────────
function authFetch(path, opts = {}) {
  const token = localStorage.getItem('cg_token')
  return fetch(path, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  })
}

// ── Toggle ────────────────────────────────────────────────────────────────────
function Toggle({ value, onChange, label, desc }) {
  return (
    <div className="flex items-center justify-between gap-3 py-4 border-b border-slate-100 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-base font-medium text-slate-800">{label}</p>
        {desc && <p className="text-sm text-slate-500 mt-0.5 leading-snug">{desc}</p>}
      </div>
      <button
        onClick={() => onChange(!value)}
        className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none ${
          value ? 'bg-blue-600' : 'bg-slate-200'
        }`}
      >
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${
          value ? 'translate-x-6' : 'translate-x-1'
        }`} />
      </button>
    </div>
  )
}

// ── Status badge ──────────────────────────────────────────────────────────────
function VerifiedBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
      </svg>
      Verified
    </span>
  )
}

function UnverifiedBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
      Not verified
    </span>
  )
}

// ── Profile Tab ───────────────────────────────────────────────────────────────
function ProfileTab({ user }) {
  const { setUser } = useApp()
  const normalizedSit = Array.isArray(user?.situation) ? user.situation : (user?.situation ? [user.situation] : [])
  const [form, setForm]       = useState({ name: user?.name || '', state: user?.state || '', situation: normalizedSit })
  const [saving, setSaving]   = useState(false)
  const [msg, setMsg]         = useState('')

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const toggleSituation = (value) => {
    set('situation', form.situation.includes(value)
      ? form.situation.filter(v => v !== value)
      : [...form.situation, value])
  }

  const handleSave = async () => {
    setSaving(true); setMsg('')
    try {
      const res  = await authFetch('/api/user/settings', { method: 'PUT', body: JSON.stringify({ name: form.name, state: form.state, situation: form.situation }) })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setUser(prev => ({ ...prev, ...data.settings }))
      setMsg('Saved!')
    } catch (e) { setMsg(e.message || 'Failed to save.') }
    finally { setSaving(false) }
  }

  return (
    <div className="space-y-4">
      {/* Name + State card */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-4 space-y-3">
          <Input label="Display Name" value={form.name} onChange={e => set('name', e.target.value)} />
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
            <select
              value={form.state}
              onChange={e => set('state', e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select state…</option>
              {['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware',
                'Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky',
                'Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi',
                'Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico',
                'New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania',
                'Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
                'Virginia','Washington','West Virginia','Wisconsin','Wyoming'].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Email row */}
        <div className="border-t border-slate-100 px-4 py-3">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-0.5">Email</p>
          <p className="text-sm font-medium text-slate-800">{user?.email}</p>
        </div>
      </div>

      {/* My Situation card */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <p className="text-sm font-semibold text-slate-800 mb-1">My Situation</p>
        <p className="text-xs text-slate-500 mb-3">Select all that apply. This helps tailor AI guidance to your needs.</p>
        <div className="flex flex-col gap-2">
          {SITUATIONS.map(s => {
            const on = form.situation.includes(s.value)
            return (
              <button
                key={s.value}
                onClick={() => toggleSituation(s.value)}
                className={[
                  'w-full flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all duration-150',
                  on ? 'border-brand-500 bg-brand-50' : 'border-slate-200 bg-white hover:border-brand-300',
                ].join(' ')}
              >
                <span className="text-xl flex-shrink-0 mt-0.5">{s.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 text-sm">{s.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{s.desc}</p>
                </div>
                <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 transition-colors ${on ? 'bg-brand-500 border-brand-500' : 'border-slate-300'}`}>
                  {on && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3} className="w-3 h-3">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {msg && (
        <p className={`text-sm text-center font-medium ${msg === 'Saved!' ? 'text-green-600' : 'text-red-600'}`}>
          {msg}
        </p>
      )}
      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full py-3.5 rounded-2xl bg-brand-700 text-white font-bold text-sm disabled:opacity-50 active:scale-[0.98] transition-transform"
      >
        {saving ? 'Saving…' : 'Save Profile'}
      </button>
    </div>
  )
}

// ── Notifications Tab ─────────────────────────────────────────────────────────
function NotificationsTab({ user }) {
  const { setUser } = useApp()
  const [phone, setPhone]         = useState(user?.phone || '')
  const [notifEmail, setNotifEmail] = useState(!!user?.notif_email)
  const [notifSms, setNotifSms]   = useState(!!user?.notif_sms)
  const [saving, setSaving]       = useState(false)
  const [msg, setMsg]             = useState('')

  // Phone verification flow
  const [otpSent, setOtpSent]       = useState(false)
  const [otpCode, setOtpCode]       = useState('')
  const [sendingOtp, setSendingOtp] = useState(false)
  const [confirmingOtp, setConfirmingOtp] = useState(false)
  const [phoneMsg, setPhoneMsg]     = useState('')
  const [phoneVerified, setPhoneVerified] = useState(!!user?.phone_verified)

  const handleSave = async () => {
    setSaving(true); setMsg('')
    try {
      const res  = await authFetch('/api/user/settings', {
        method: 'PUT',
        body: JSON.stringify({ phone: phone || null, notif_email: notifEmail, notif_sms: notifSms }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setUser(prev => ({ ...prev, ...data.settings }))
      setMsg('Saved!')
    } catch (e) { setMsg(e.message || 'Failed to save.') }
    finally { setSaving(false) }
  }

  const handleSendOtp = async () => {
    if (!phone.trim()) return setPhoneMsg('Enter a phone number first.')
    setSendingOtp(true); setPhoneMsg(''); setOtpCode('')
    try {
      const res  = await authFetch('/api/auth/send-phone-otp', {
        method: 'POST',
        body: JSON.stringify({ phone: phone.trim() }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setOtpSent(true)
      setPhoneMsg(`Code sent to ${data.phone}`)
    } catch (e) { setPhoneMsg(e.message || 'Failed to send SMS.') }
    finally { setSendingOtp(false) }
  }

  const handleConfirmOtp = async () => {
    if (otpCode.length !== 6) return setPhoneMsg('Enter the 6-digit code.')
    setConfirmingOtp(true); setPhoneMsg('')
    try {
      const res  = await authFetch('/api/auth/confirm-phone', {
        method: 'POST',
        body: JSON.stringify({ code: otpCode }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setPhoneVerified(true)
      setOtpSent(false)
      setPhoneMsg('Phone number verified!')
      setUser(prev => ({ ...prev, phone: data.phone, phone_verified: 1 }))
    } catch (e) { setPhoneMsg(e.message || 'Invalid code.') }
    finally { setConfirmingOtp(false) }
  }

  const isPhoneDirty = phone.trim() !== (user?.phone || '')

  return (
    <div className="space-y-4">
      {/* Phone number */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-4 pt-4 pb-3">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-semibold text-slate-700">Phone Number</p>
            {phoneVerified && !isPhoneDirty ? <VerifiedBadge /> : phone ? <UnverifiedBadge /> : null}
          </div>
          <p className="text-xs text-slate-500 mb-3">Required for SMS notifications and SMS-based 2FA.</p>

          <div className="flex gap-2">
            <input
              type="tel"
              value={phone}
              onChange={e => { setPhone(e.target.value); setOtpSent(false); setPhoneMsg('') }}
              placeholder="+15551234567"
              className="flex-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {(!phoneVerified || isPhoneDirty) && (
              <button
                onClick={handleSendOtp}
                disabled={sendingOtp || !phone.trim()}
                className="px-4 py-2.5 rounded-xl bg-brand-700 text-white text-sm font-semibold disabled:opacity-40 whitespace-nowrap"
              >
                {sendingOtp ? '…' : otpSent ? 'Resend' : 'Verify'}
              </button>
            )}
          </div>

          {/* OTP entry */}
          {otpSent && (
            <div className="mt-3 space-y-2">
              <p className="text-xs text-slate-500">Enter the 6-digit code sent to your phone:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="flex-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-mono tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleConfirmOtp}
                  disabled={confirmingOtp || otpCode.length !== 6}
                  className="px-4 py-2.5 rounded-xl bg-green-600 text-white text-sm font-semibold disabled:opacity-40"
                >
                  {confirmingOtp ? '…' : 'Confirm'}
                </button>
              </div>
            </div>
          )}

          {phoneMsg && (
            <p className={`text-xs font-medium mt-2 ${
              phoneMsg.includes('verified') || phoneMsg.includes('sent')
                ? 'text-green-700' : 'text-red-600'
            }`}>
              {phoneMsg}
            </p>
          )}
        </div>
      </div>

      {/* Notification toggles */}
      <div className="bg-white rounded-2xl border border-slate-200 px-4">
        <p className="text-sm font-semibold text-slate-700 pt-4 pb-2">Notification Channels</p>
        <Toggle
          label="Email notifications"
          desc="Co-parent updates, new messages, and alerts."
          value={notifEmail}
          onChange={setNotifEmail}
        />
        <Toggle
          label="SMS notifications"
          desc="Time-sensitive alerts via text message."
          value={notifSms}
          onChange={v => {
            if (v && !phone) { setMsg('Add and verify a phone number first.'); return }
            setNotifSms(v)
          }}
        />
      </div>

      {msg && (
        <p className={`text-sm text-center font-medium ${msg === 'Saved!' ? 'text-green-600' : 'text-red-600'}`}>
          {msg}
        </p>
      )}
      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full py-3.5 rounded-2xl bg-brand-700 text-white font-bold text-sm disabled:opacity-50 active:scale-[0.98] transition-transform"
      >
        {saving ? 'Saving…' : 'Save Notifications'}
      </button>
    </div>
  )
}

// ── Security Tab ──────────────────────────────────────────────────────────────
function SecurityTab({ user }) {
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' })
  const [savingPw, setSavingPw] = useState(false)
  const [msgPw, setMsgPw]       = useState('')

  // Biometric state
  const [bioInfo, setBioInfo]       = useState({ available: false, type: 'none' })
  const [bioEnabled, setBioEnabled] = useState(isBiometricEnabled())
  const [bioWorking, setBioWorking] = useState(false)
  const [bioMsg, setBioMsg]         = useState('')

  useEffect(() => {
    getBiometryInfo().then(setBioInfo)
  }, [])

  async function handleBioToggle() {
    setBioMsg('')
    if (bioEnabled) {
      // Turning OFF — no auth needed, just disable
      disableBiometric()
      setBioEnabled(false)
      setBioMsg(`${biometryLabel(bioInfo.type)} disabled.`)
    } else {
      // Turning ON — verify biometric first to confirm it works
      setBioWorking(true)
      const success = await bioAuthenticate(`Confirm ${biometryLabel(bioInfo.type)} to enable it for login`)
      setBioWorking(false)
      if (success) {
        enableBiometric()
        setBioEnabled(true)
        setBioMsg(`${biometryLabel(bioInfo.type)} enabled! Use it next time you sign in.`)
      } else {
        setBioMsg('Biometric check failed or was cancelled.')
      }
    }
  }

  const isEmailVerified = !!user?.email_verified
  const isPhoneVerified = !!user?.phone_verified

  const savePassword = async () => {
    if (pwForm.newPw !== pwForm.confirm) { setMsgPw("Passwords don't match."); return }
    if (pwForm.newPw.length < 8) { setMsgPw('Password must be at least 8 characters.'); return }
    setSavingPw(true); setMsgPw('')
    try {
      const res  = await authFetch('/api/user/settings', {
        method: 'PUT',
        body: JSON.stringify({ password: pwForm.current, new_password: pwForm.newPw }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setPwForm({ current: '', newPw: '', confirm: '' })
      setMsgPw('Password updated!')
    } catch (e) { setMsgPw(e.message || 'Failed.') }
    finally { setSavingPw(false) }
  }

  return (
    <div className="space-y-4">
      {/* Verification status summary */}
      <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
        <div className="flex items-center justify-between px-4 py-3">
          <p className="text-sm text-slate-700">Email</p>
          {isEmailVerified ? <VerifiedBadge /> : <UnverifiedBadge />}
        </div>
        <div className="flex items-center justify-between px-4 py-3">
          <p className="text-sm text-slate-700">Phone</p>
          {isPhoneVerified ? <VerifiedBadge /> : (user?.phone ? <UnverifiedBadge /> : <span className="text-xs text-slate-500">Not set</span>)}
        </div>
      </div>

      {/* Biometric login */}
      {bioInfo.available && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{biometryIcon(bioInfo.type)}</span>
              <div>
                <p className="text-sm font-semibold text-slate-700">{biometryLabel(bioInfo.type)} Login</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {bioEnabled ? 'Enabled — sign in without your password' : 'Sign in faster without typing your password'}
                </p>
              </div>
            </div>
            <button
              onClick={handleBioToggle}
              disabled={bioWorking}
              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                bioEnabled ? 'bg-brand-700' : 'bg-slate-300'
              } disabled:opacity-50`}
              aria-label={`${bioEnabled ? 'Disable' : 'Enable'} ${biometryLabel(bioInfo.type)}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                bioEnabled ? 'translate-x-5' : 'translate-x-0.5'
              }`} />
            </button>
          </div>
          {bioWorking && (
            <p className="text-xs text-brand-700 mt-2 font-medium">Waiting for {biometryLabel(bioInfo.type)}…</p>
          )}
          {bioMsg && (
            <p className={`text-xs mt-2 font-medium ${bioMsg.includes('enabled') ? 'text-green-600' : bioMsg.includes('disabled') ? 'text-slate-500' : 'text-red-600'}`}>
              {bioMsg}
            </p>
          )}
        </div>
      )}

      {/* Change password */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-slate-700">Change Password</h3>
        <Input
          label="Current Password"
          type="password"
          value={pwForm.current}
          onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))}
        />
        <Input
          label="New Password"
          type="password"
          value={pwForm.newPw}
          onChange={e => setPwForm(p => ({ ...p, newPw: e.target.value }))}
        />
        <Input
          label="Confirm New Password"
          type="password"
          value={pwForm.confirm}
          onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))}
        />
        {msgPw && (
          <p className={`text-sm font-medium ${msgPw.includes('!') ? 'text-green-600' : 'text-red-600'}`}>{msgPw}</p>
        )}
        <button
          onClick={savePassword}
          disabled={savingPw || !pwForm.current || !pwForm.newPw || !pwForm.confirm}
          className="w-full py-3 rounded-xl bg-brand-700 text-white font-bold text-sm disabled:opacity-50"
        >
          {savingPw ? 'Updating…' : 'Update Password'}
        </button>
      </div>
    </div>
  )
}

// ── Appearance Tab ─────────────────────────────────────────────────────────────
function AppearanceTab() {
  const { theme, setTheme } = useTheme()

  const options = [
    {
      key: 'light',
      icon: '☀️',
      label: 'Light',
      desc: 'Always use the light theme',
    },
    {
      key: 'dark',
      icon: '🌙',
      label: 'Dark',
      desc: 'Always use the dark theme',
    },
    {
      key: 'system',
      icon: '⚙️',
      label: 'System',
      desc: 'Match your device display settings',
    },
  ]

  return (
    <div className="flex flex-col gap-5">
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <p className="text-sm font-semibold text-slate-700 px-4 pt-4 pb-2">Display Mode</p>
        <div className="divide-y divide-slate-100">
          {options.map(opt => (
            <button
              key={opt.key}
              onClick={() => setTheme(opt.key)}
              className="w-full flex items-center gap-4 px-4 py-3.5 text-left hover:bg-slate-50 dark:hover:bg-slate-700 active:bg-slate-100 dark:active:bg-slate-600 transition-colors"
            >
              <span className="text-2xl w-8 text-center flex-shrink-0">{opt.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800">{opt.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{opt.desc}</p>
              </div>
              <div className={[
                'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all',
                theme === opt.key
                  ? 'border-brand-700 bg-brand-700 dark:border-blue-400 dark:bg-blue-500'
                  : 'border-slate-300 dark:border-slate-500 bg-transparent',
              ].join(' ')}>
                {theme === opt.key && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-brand-50 border border-brand-100 rounded-2xl px-4 py-3.5 flex gap-3 items-start">
        <span className="text-xl flex-shrink-0">💡</span>
        <p className="text-xs text-brand-700 leading-relaxed">
          <strong>System</strong> is the default — GoodGuardian automatically follows your device's current display mode. Switch it any time here.
        </p>
      </div>
    </div>
  )
}

// ── Dashboard Tab ─────────────────────────────────────────────────────────────
const DASH_SECTIONS = [
  {
    id: 'evidence', label: 'Evidence & Records',
    items: [
      { id: 'comms',      emoji: '💬', label: 'Comms Log' },
      { id: 'events',     emoji: '📋', label: 'Event Log' },
      { id: 'violations', emoji: '⚠️', label: 'Violation Tracker' },
      { id: 'timeline',   emoji: '🗂️', label: 'Case Timeline' },
    ],
  },
  {
    id: 'legal', label: 'Legal',
    items: [
      { id: 'orders',        emoji: '⚖️', label: 'Court Orders' },
      { id: 'documents',     emoji: '📄', label: 'Documents' },
      { id: 'cps',           emoji: '🛡️', label: 'CPS Tracker' },
      { id: 'attorney-match',emoji: '🏛️', label: 'Find an Attorney' },
    ],
  },
  {
    id: 'parenting', label: 'Parenting',
    items: [
      { id: 'parenting-time', emoji: '⏱️', label: 'Parenting Time' },
      { id: 'children',       emoji: '👶', label: 'Children' },
      { id: 'medical',        emoji: '🏥', label: 'Medical Records' },
      { id: 'wellbeing',      emoji: '😊', label: 'Wellbeing Log' },
    ],
  },
  {
    id: 'finances', label: 'Finances',
    items: [
      { id: 'expenses',          emoji: '🧾', label: 'Expense Log' },
      { id: 'expense-analytics', emoji: '📊', label: 'Expense Analytics' },
      { id: 'child-support',     emoji: '💰', label: 'Child Support Calc' },
      { id: 'credit',            emoji: '💳', label: 'Credit Score' },
    ],
  },
  {
    id: 'ai', label: 'AI Legal Tools',
    items: [
      { id: 'ask',          emoji: '✨', label: 'Ask Anything' },
      { id: 'coach',        emoji: '🎓', label: 'Court Prep Coach' },
      { id: 'case-analyst', emoji: '🔍', label: 'Case Analyst' },
    ],
  },
]

// ── Situation → recommended module IDs ───────────────────────────────────────
const SITUATION_RECS = {
  divorcing: {
    label: 'Going through divorce',
    emoji: '⚖️',
    ids: ['comms', 'events', 'violations', 'orders', 'documents', 'expenses', 'child-support', 'attorney-match', 'case-analyst', 'ask'],
    reason: 'Track communications, document events, manage court orders and expenses — all critical during divorce.',
  },
  custody: {
    label: 'Custody dispute',
    emoji: '👨‍👧',
    ids: ['comms', 'events', 'violations', 'timeline', 'orders', 'documents', 'parenting-time', 'children', 'attorney-match', 'coach', 'ask'],
    reason: 'Build your custody case with evidence logs, parenting time records, and court prep tools.',
  },
  cps: {
    label: 'CPS involvement',
    emoji: '🏛️',
    ids: ['cps', 'comms', 'events', 'documents', 'children', 'medical', 'wellbeing', 'coach', 'ask'],
    reason: 'Document your child\'s wellbeing, log all CPS interactions, and prepare for interviews.',
  },
  coparenting: {
    label: 'Managing co-parenting',
    emoji: '🤝',
    ids: ['comms', 'parenting-time', 'children', 'expenses', 'calendar', 'events', 'orders', 'ask'],
    reason: 'Stay organized with shared schedules, expense tracking, and communication records.',
  },
}

function getRecommendedIds(situation) {
  const situations = Array.isArray(situation) ? situation : (situation ? [situation] : [])
  const merged = new Set()
  situations.forEach(s => { SITUATION_RECS[s]?.ids.forEach(id => merged.add(id)) })
  return merged
}

function DashboardTab() {
  const { user } = useApp()
  const [prefs, setPrefs] = useState(getDashPrefs)
  const [recApplied, setRecApplied] = useState(false)

  const situations = Array.isArray(user?.situation) ? user.situation : (user?.situation ? [user.situation] : [])
  const recIds = getRecommendedIds(user?.situation)
  const hasRecs = situations.length > 0 && recIds.size > 0

  const allIds = DASH_SECTIONS.flatMap(s => s.items).map(i => i.id)
  const currentEnabled = new Set(allIds.filter(id => prefs[id] !== false))
  const recAlreadyApplied = hasRecs && [...recIds].every(id => currentEnabled.has(id))

  function applyRecommendations() {
    const next = {}
    allIds.forEach(id => { next[id] = recIds.has(id) })
    saveDashPrefs(next)
    setPrefs(next)
    setRecApplied(true)
    setTimeout(() => setRecApplied(false), 2500)
  }

  function toggle(id) {
    setPrefs(prev => {
      const next = { ...prev, [id]: !prev[id] }
      saveDashPrefs(next)
      return next
    })
  }

  function resetDefaults() {
    const defaults = {}
    DASH_SECTIONS.flatMap(s => s.items).forEach(item => {
      defaults[item.id] = DASH_DEFAULT_MODULES.has(item.id)
    })
    saveDashPrefs(defaults)
    setPrefs(defaults)
  }

  const enabledCount = Object.values(prefs).filter(Boolean).length

  return (
    <div className="flex flex-col gap-5">
      {/* Situation-based recommendation banner */}
      {hasRecs && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-brand-200 dark:border-brand-700 overflow-hidden">
          <div className="px-4 py-3 bg-brand-50 dark:bg-brand-900/30 border-b border-brand-100 dark:border-brand-800 flex items-center gap-2">
            <span className="text-base">✨</span>
            <p className="text-sm font-bold text-brand-800 dark:text-brand-200 flex-1">Recommended for your situation</p>
          </div>
          <div className="px-4 py-3 flex flex-col gap-3">
            <div className="flex flex-wrap gap-1.5">
              {situations.map(s => SITUATION_RECS[s] && (
                <span key={s} className="text-xs font-semibold bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 px-2.5 py-1 rounded-full">
                  {SITUATION_RECS[s].emoji} {SITUATION_RECS[s].label}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-1">
              {[...recIds].map(id => {
                const item = DASH_SECTIONS.flatMap(s => s.items).find(i => i.id === id)
                if (!item) return null
                return (
                  <span key={id} className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded-full">
                    {item.emoji} {item.label}
                  </span>
                )
              })}
            </div>
            {recAlreadyApplied ? (
              <p className="text-xs text-green-600 dark:text-green-400 font-semibold">✓ Recommendations already applied</p>
            ) : (
              <button
                onClick={applyRecommendations}
                className="w-full py-2.5 rounded-xl bg-brand-700 text-white text-sm font-bold active:scale-[0.98] transition-transform"
              >
                {recApplied ? '✓ Applied!' : 'Apply Recommendations'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-brand-50 dark:bg-brand-900/20 border border-brand-100 dark:border-brand-800 rounded-2xl px-4 py-3.5 flex gap-3 items-start">
        <span className="text-xl flex-shrink-0">🏠</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-brand-800 dark:text-brand-200 leading-snug">
            Choose what appears on your dashboard
          </p>
          <p className="text-xs text-brand-600 dark:text-brand-400 mt-0.5 leading-snug">
            {enabledCount} section{enabledCount !== 1 ? 's' : ''} visible · Tap to show or hide
          </p>
        </div>
      </div>

      {DASH_SECTIONS.map(section => {
        const allOn  = section.items.every(i => prefs[i.id] !== false)
        const noneOn = section.items.every(i => prefs[i.id] === false)
        return (
          <div key={section.id} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            {/* Section header with bulk toggle */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-700">
              <p className="text-sm font-bold text-slate-700 dark:text-slate-200">{section.label}</p>
              <button
                onClick={() => {
                  const next = { ...prefs }
                  section.items.forEach(i => { next[i.id] = !allOn })
                  saveDashPrefs(next)
                  setPrefs(next)
                }}
                className={`text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${
                  allOn
                    ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
                    : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                }`}
              >
                {allOn ? 'Hide all' : 'Show all'}
              </button>
            </div>

            {/* Items */}
            {section.items.map((item, i) => {
              const on = prefs[item.id] !== false
              const isRec = hasRecs && recIds.has(item.id)
              return (
                <button
                  key={item.id}
                  onClick={() => toggle(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    i < section.items.length - 1 ? 'border-b border-slate-100 dark:border-slate-700' : ''
                  } ${on ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-900/40'}`}
                >
                  <span className={`text-xl flex-shrink-0 transition-opacity ${on ? 'opacity-100' : 'opacity-30'}`}>
                    {item.emoji}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium transition-colors ${
                      on ? 'text-slate-800 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500'
                    }`}>
                      {item.label}
                    </p>
                    {isRec && (
                      <p className="text-[10px] font-semibold text-brand-600 dark:text-brand-400 mt-0.5">✨ Recommended</p>
                    )}
                  </div>
                  {/* Toggle pill */}
                  <div className={`relative inline-flex h-6 w-10 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${
                    on ? 'bg-brand-600' : 'bg-slate-200 dark:bg-slate-600'
                  }`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                      on ? 'translate-x-5' : 'translate-x-1'
                    }`} />
                  </div>
                </button>
              )
            })}
          </div>
        )
      })}

      {/* Reset */}
      <button
        onClick={resetDefaults}
        className="text-sm text-slate-500 dark:text-slate-400 font-medium py-2 text-center active:opacity-60 transition-opacity"
      >
        Reset to defaults
      </button>
    </div>
  )
}

// ── Import Tab ────────────────────────────────────────────────────────────────
const IMPORT_PLATFORMS = [
  {
    id: 'ofw',
    name: 'Our Family Wizard',
    short: 'OFW',
    emoji: '📗',
    desc: 'Export message board history as a PDF and import it here.',
  },
  {
    id: 'talkingparents',
    name: 'TalkingParents',
    short: 'TalkingParents',
    emoji: '💬',
    desc: 'Download your message PDF from the TalkingParents app.',
  },
  {
    id: 'other',
    name: 'Other Platform',
    short: 'Other',
    emoji: '📄',
    desc: 'Any co-parenting app that can export a PDF of message history.',
  },
]

function ImportTab() {
  const navigate = useNavigate()

  return (
    <div className="space-y-4">
      {/* Header card */}
      <div className="card p-4">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-2xl">📥</span>
          <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Import from Another Platform</h3>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
          Already using OFW, TalkingParents, or another co-parenting app?
          Export your message history as a PDF and GoodGuardian will automatically
          extract and add those records to your Communications Log.
        </p>
      </div>

      {/* Platform cards */}
      <div className="space-y-3">
        {IMPORT_PLATFORMS.map(p => (
          <button
            key={p.id}
            onClick={() => navigate('/import')}
            className="w-full card p-4 flex items-center gap-4 text-left active:scale-[0.98] transition-transform"
          >
            <span className="text-3xl flex-shrink-0">{p.emoji}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{p.name}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">{p.desc}</p>
            </div>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 text-slate-400 flex-shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
            </svg>
          </button>
        ))}
      </div>

      {/* CTA */}
      <Button variant="primary" size="lg" fullWidth onClick={() => navigate('/import')}>
        Start Import
      </Button>

      <div className="disclaimer text-xs">
        <strong>Privacy:</strong> Uploaded files are processed by AI to extract records and are not permanently stored on our servers.
      </div>
    </div>
  )
}

// ── Settings Screen ───────────────────────────────────────────────────────────
export default function Settings() {
  const { user } = useApp()
  const location = useLocation()
  const [tab, setTab] = useState(location.state?.tab || 'profile')

  const tabs = [
    { key: 'profile',    label: 'Profile',    icon: '👤' },
    { key: 'notifs',     label: 'Alerts',     icon: '🔔' },
    { key: 'appearance', label: 'Display',    icon: '🎨' },
    { key: 'security',   label: 'Security',   icon: '🔐' },
    { key: 'dashboard',  label: 'Dashboard',  icon: '🏠' },
    { key: 'import',     label: 'Import',     icon: '📥' },
  ]

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <AppHeader title="Settings" />

      {/* Tab bar */}
      <div className="flex border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex-shrink-0 overflow-x-auto no-scrollbar">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 min-w-[72px] flex flex-col items-center gap-0.5 py-3 text-xs font-semibold border-b-2 transition-colors ${
              tab === t.key
                ? 'border-brand-700 text-brand-700 dark:border-blue-400 dark:text-blue-400'
                : 'border-transparent text-slate-500 dark:text-slate-400'
            }`}
          >
            <span className="text-base">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-5">
        {tab === 'profile'    && <ProfileTab       user={user} />}
        {tab === 'notifs'     && <NotificationsTab user={user} />}
        {tab === 'appearance' && <AppearanceTab />}
        {tab === 'security'   && <SecurityTab      user={user} />}
        {tab === 'dashboard'  && <DashboardTab />}
        {tab === 'import'     && <ImportTab />}
      </div>
    </div>
  )
}
